// Quote Builder — shared catalogue row classifier + description composer.
//
// WHY THIS EXISTS (2026-08-28). The QB has two search boxes — the left
// PRODUCTS panel (products-panel.ts) and the right-column "Add item" box
// (add-item-search.ts) — and until now each decided independently what a
// catalogue row *is* and what it should read as on the quote. They disagreed,
// and both were wrong in the same underlying way: they classified a product by
// its NAME.
//
// That works only for suppliers whose product names are prose. It is not true
// of the two biggest catalogues:
//
//   TKC   name = "110 X  596"   category = "Vivo Gloss White Doors"
//                               doorRange = "Vivo Gloss"  colour = "White"
//   PWS   name = "115 x 597mm (slab)"  category = "Fascia"
//                               doorRange = "Arlo Matt Dove Grey"  colour = "Dove Grey"
//
// A TKC door's name is its SIZE; its identity lives in the category. So the old
// name regex saw "110 X 596", matched no rule, and filed every TKC door under
// "Accessories & Sundries" — which is why doors opened the sundries picker and
// why a searched door line read as a bare size string.
//
// The reliable fields (category, doorRange, colour) are already on every
// /api/catalogue/search row and were already being read for discount
// resolution. This module reads them for classification and description too.
//
// STRUCTURAL NOTE. TKC splits style from finish (doorRange "Vivo Gloss" +
// colour "White"); PWS combines them (doorRange "Arlo Matt Dove Grey", colour
// "Dove Grey" — a substring of it). composeCatalogueDesc handles both without
// doubling up, which is the difference Steve spotted between the two suppliers.

import type { ItemCategory, TkcDisplayCategory } from "./types";
import { TKC_CATEGORY_TO_ITEM } from "./types";

/** The fields a classifier decision is made from. Every catalogue-backed
 *  search hit can supply these; DTN/LDL supply `category` + `name` only. */
export interface CatalogueRowFacts {
  /** Product name / description. */
  name: string;
  /** catalogue_categories.name for PG rows, or the supplier's own category
   *  string for the live-API tabs (DTN: "Cabinet" | "Door" | …). */
  category: string;
  /** specification.doorRange. */
  doorRange: string;
  /** specification.colour. */
  colour: string;
}

/** Verbatim regex classifier from source kos-qb-v3.js:1685–1704. Maps a
 *  product NAME to one of the TKC display categories.
 *
 *  DEMOTED TO A FALLBACK (2026-08-28). This was the primary classifier and is
 *  now step 9 of 10 in classifyCatalogueHit — reached only when the category
 *  name carries no usable signal. It is still the right tool there: it is
 *  accurate on rows whose name IS prose (TKC Handles / Drawers / Sinks & Taps,
 *  every DTN and LDL row, the retailer's own uploads), which is exactly the
 *  ~700 TKC rows and both live-API catalogues that reach it.
 *
 *  Still used directly by the TKC tab's chip strip for display grouping, which
 *  is a separate concern from what category the LINE gets. */
export function tkcInferCategory(name: string): TkcDisplayCategory {
  const n = (name || "").toUpperCase();
  if (/\bDOOR\b|INTEG APP DOOR/.test(n)) return "Doors";
  // Boxed drawers — checked before Storage/Cabinets (which also use DRAWER*).
  // Reliable section detection is category-driven server-side; this name rule
  // only buckets result rows whose name does carry a drawer keyword.
  if (/BOXED DRAWER|BLUM BOXED|DRAWER BOX|LEGRABOX|TANDEMBOX/.test(n)) return "Boxed Drawers";
  if (/CARCASE/.test(n)) return "Quick Cabs";
  if (/\bHANDLE\b|\bKNOB\b|\bCUP\b|\bBAR HANDLE\b|\bT-BAR\b|\bBRASS\b|\bCHROME\b/.test(n) && !/CABINET|BASE|WALL|TALL/.test(n)) return "Handles";
  if (/WORKTOP|LAMINATE|QUARTZ|GRANITE|SOLID OAK|SOLID SURFACE/.test(n)) return "Worktops";
  if (/SINK|BOWL|UNDERMOUNT/.test(n) && !/UNDERSINK|PULL OUT/.test(n)) return "Sinks & Taps";
  if (/\bTAP\b|MIXER TAP|MONOBLOC/.test(n)) return "Sinks & Taps";
  if (/\bOVEN\b|\bHOB\b|EXTRACTOR|FRIDGE|FREEZER|DISHWASHER|MICROWAVE|COOKER|APPLIANCE/.test(n)) return "Appliances";
  if (/LIGHT|LIGHTING|DOWNLIGHT|LED|SPOTLIGHT|PELMET LIGHT|PLINTH LIGHT/.test(n)) return "Lighting";
  if (/PLINTH|SKIRTING|CORNICE|PELMET|PILASTER|QUADRANT|SCOTIA/.test(n)) return "Plinth & Cornices";
  if (/END PANEL|BACK PANEL|B\/BAR PANEL|BREAKFAST BAR PANEL|FILLER PANEL|DECOR PANEL/.test(n)) return "Panels";
  if (/\bPANEL\b/.test(n) && !/CONTROL PANEL/.test(n)) return "Panels";
  if (/BIN|CAROUSEL|PULL OUT|DRAWER BOX|STORAGE|LARDER PULL|MAGIC CORNER|CARGO|WIRE BASKET/.test(n)) return "Storage Solutions";
  if (/DRAWER PACK|DRAWER BASE|DRAWER UNIT/.test(n)) return "Cabinets";
  if (/BASE|SINK BASE|CORNER BASE|BLIND CORNER|HIGHLINE BASE/.test(n)) return "Cabinets";
  if (/WALL UNIT|WALL CABINET|BRIDGE|CORNER WALL/.test(n)) return "Cabinets";
  if (/TALL UNIT|LARDER|HOUSING|PANTRY|TOWER/.test(n)) return "Cabinets";
  if (/CABINET|UNIT|CARCASS/.test(n)) return "Cabinets";
  return "Accessories & Sundries";
}

/** A door's product name in both PG catalogues is its size — "110 X  596"
 *  (TKC) or "139 x 447mm" (PWS). Used as a door signal for the handful of TKC
 *  categories that are a bare range name ("Cambridge", "Stratto White") and so
 *  carry no "Doors" keyword to match on. */
const SIZE_NAME_RE = /^\s*\d{2,4}\s*[x×*]\s*\d{2,4}/i;

/** "… Panels & Accessories" (TKC), "Fascia Accessories" (PWS), "Accessory"
 *  (DTN). A row in one of these is definitively NOT a door, whatever its
 *  doorRange says — a Cambridge plate rack carries doorRange "Cambridge". */
const ACCESSORY_CAT_RE = /(panels?\s*&\s*)?accessor(y|ies)\s*$/i;

/** TKC's Vero ranges are named "Vero Accessories <finish>" — the accessory
 *  marker is a PREFIX here, so ACCESSORY_CAT_RE (anchored at the end) misses
 *  them. Matches the existing TKC_CATEGORY_TO_ITEM["Vero Accessories"] intent. */
const VERO_CAT_RE = /^\s*vero\s+accessories\b/i;

/**
 * Decide the canonical ItemCategory for a catalogue search hit.
 *
 * Ordered rules over the CATEGORY name, with accessory markers tested BEFORE
 * the door rule — that ordering is the whole point. "Vivo Gloss White Panels &
 * Accessories" and "Fascia Accessories" both contain a door-ish keyword and
 * both carry a doorRange, so any door test that ran first would swallow 6,697
 * plinths, cornices, plate racks and end panels into `doors`.
 *
 * Accessory markers do not themselves pick a category — they only rule OUT
 * door/cabinet and defer to the name regex, because an accessory group is a
 * genuine mix (a "Curved Plinth" is plinth, an "End panel" is panels, a "Plate
 * Rack" is sundries) and one blanket answer would be wrong for most of it.
 *
 * Validated against all 39,949 catalogue_products rows in tkos_dev: category
 * rules alone resolve 100% of PWS and 97.4% of TKC; the ~700 TKC rows that
 * reach the name regex are the prose-named ones (Handles, Drawers, Sinks &
 * Taps, Lighting …) where it is accurate.
 */
/**
 * Exact category → ItemCategory lookup, tried before any keyword rule.
 *
 * WHY A TABLE AND NOT MORE REGEX (2026-09-09). The PWS Feb-26 price list added
 * 14 non-door categories (2,602 rows). Run through the keyword rules below they
 * scattered badly, because those rules read a product NAME and PWS names are
 * spec strings full of colour and material words:
 *
 *   "Worksurfaces"        → sundries   (the rule tests /worktop/, PWS says
 *                                       "Worksurfaces" — all 46 rows wrong)
 *   "Cabinet Components"  → cabinets   (hinges and latches, matched on the
 *                                       word "Cabinet" — all 177 wrong, and
 *                                       `cabinets` drives direct-add)
 *   "Tap"                 → handles    ("Live L spout tap, Chrome" hit the
 *                                       \bCHROME\b handle rule)
 *   "Storage System"      → 8 different buckets, e.g. "Chrome Classic 300mm
 *                                       larder unit" → handles
 *
 * These are genuinely TYPED categories — one value per product type, already
 * normalised in the source file — so an exact lookup is both correct and
 * deterministic, where a keyword rule is a guess. That is the same reasoning
 * the module already applies to TKC: prefer the structured field, fall back to
 * the name only when there is no structure to read.
 *
 * Keyed lowercase; a category absent here falls through to the rules below, so
 * adding a supplier costs nothing until its categories actually need it.
 */
const CATEGORY_TO_ITEM: Readonly<Record<string, ItemCategory>> = {
  // PWS doors — already resolved by the keyword rules, listed so the door
  // categories are visible alongside the rest rather than looking omitted.
  "fascia": "doors",

  // PWS non-door categories (Feb-26 list).
  "worksurfaces": "worktops",
  "handles": "handles",
  "handleless rail": "handles",
  "sink": "sundries",          // matches TKC, where "Sinks & Taps" → sundries
  "tap": "sundries",
  "electrical": "lighting",    // 12V drivers, strips, sensors, extension leads
  "drawer system": "storage",  // LEGRABOX / MOVENTO / TANDEM runners + boxes
  "drawers": "storage",
  "drawer organisation": "storage",
  "storage system": "storage", // larder pull-outs, magic corners, carousels
  "waste system": "storage",
  "wardrobe": "storage",
  "bedroom internals": "storage",
  // Hinges, latches, brackets, glass racks — components that go INTO a
  // cabinet, not cabinets. Deliberately not `cabinets`, which routes to the
  // cabinet flow and direct-adds.
  "cabinet components": "sundries",
};

export function classifyCatalogueHit(facts: Partial<CatalogueRowFacts>): ItemCategory {
  const cat = String(facts.category || "").trim();
  const name = String(facts.name || "");
  const doorRange = String(facts.doorRange || "").trim();

  // 0 — exact category match wins outright. Only reached for categories we
  // have actually classified by hand, so it can never make a guess worse.
  const exact = CATEGORY_TO_ITEM[cat.toLowerCase()];
  if (exact) return exact;

  // Steps 1–3: accessory family. Skip the door/cabinet rules entirely and let
  // the name decide; sundries is the floor.
  const isAccessoryFamily = ACCESSORY_CAT_RE.test(cat) || VERO_CAT_RE.test(cat);

  if (!isAccessoryFamily) {
    // 4 — carcases (TKC "White Carcases") and DTN's "Cabinet".
    if (/carcase|carcass|cabinet/i.test(cat)) return "cabinets";
    // 5 — TKC "<Range> <Colour> Doors", PWS "Fascia", DTN "Door".
    if (/\bdoors?\b/i.test(cat) || /^fascia$/i.test(cat)) return "doors";
    // 6/7 — DTN "Handle" / "Worktop"; TKC "Handles".
    if (/handle/i.test(cat)) return "handles";
    if (/worktop/i.test(cat)) return "worktops";
    // 8 — bare-range TKC categories ("Cambridge", "Stratto White") carry no
    // keyword. A row with a doorRange whose NAME is a size is a door. Guarded
    // against accessory doorRanges ("Cambridge Fjord Panels & Accessories"),
    // which sit in these same bare categories.
    if (doorRange && SIZE_NAME_RE.test(name) && !ACCESSORY_CAT_RE.test(doorRange)) {
      return "doors";
    }
  }

  // 9 — name regex, then 10 — sundries (its own terminal value).
  return TKC_CATEGORY_TO_ITEM[tkcInferCategory(name)];
}

/** Strip the trailing " Doors" / " Door" a catalogue doorRange often carries
 *  ("Cambridge Fjord Doors"). Same normalisation /api/catalogue/door-ranges
 *  already applies when it builds range cards, so descriptions composed here
 *  read the same as ones built by the door picker. */
function stripDoorSuffix(range: string): string {
  return range.replace(/\s+doors?\s*$/i, "").trim();
}

/**
 * Compose the quote-line description for a catalogue search hit.
 *
 * Both PG catalogues name a door by its SIZE, so the raw name alone reads as
 * nothing on a customer quote ("110 X  596"). The style has to be prefixed —
 * but the two catalogues store style and finish differently:
 *
 *   TKC   doorRange "Vivo Gloss"          + colour "White"      → both needed
 *   PWS   doorRange "Arlo Matt Dove Grey" + colour "Dove Grey"  → already there
 *
 * So the colour is appended only when the range does not already contain it.
 * TKC gains its finish (matching the door picker's own format at
 * door-picker.ts:830, "Vivo Gloss White 715×596mm"); PWS does not get
 * "Arlo Matt Dove Grey Dove Grey".
 *
 * Rows with no doorRange (handles, drawers, every DTN/LDL row) are returned
 * unchanged — their names are already prose.
 */
export function composeCatalogueDesc(facts: Partial<CatalogueRowFacts>): string {
  const name = String(facts.name || "").trim();
  const range = stripDoorSuffix(String(facts.doorRange || ""));
  const colour = String(facts.colour || "").trim();
  if (!range) return name || "Item";

  const style = colour && !range.toLowerCase().includes(colour.toLowerCase())
    ? `${range} ${colour}`
    : range;

  if (!name) return style;
  // Never prefix a style the name already carries.
  return name.toLowerCase().includes(style.toLowerCase()) ? name : `${style} ${name}`;
}
